import React, { createContext, useContext, useEffect, useState } from 'react';
import { API, graphqlOperation, Auth } from 'aws-amplify';

export type SubscriptionTier = 'free' | 'light' | 'premium';
export type StripeStatus = 'free' | 'light' | 'premium';

interface SubscriptionContextType {
    isLoading: boolean;
    error: string | null;
    subscriptionTier: SubscriptionTier;
    stripeStatus: StripeStatus;
    hasActiveSubscription: boolean;
    refreshSubscription: () => Promise<void>;
    isNewUserBonusActive: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stripeStatus, setStripeStatus] = useState<StripeStatus>('free');
    const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>('free');
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [isNewUserBonusActive, setIsNewUserBonusActive] = useState(false);

    const fetchSubscriptionStatus = async () => {
        console.log('[SubscriptionContext] fetchSubscriptionStatus called'); // Log: Function entry
        setIsLoading(true);
        try {
            console.log('[SubscriptionContext] Attempting Auth.currentAuthenticatedUser()'); // Log: Before Auth call
            const currentUser = await Auth.currentAuthenticatedUser();
            const userId = currentUser.attributes.sub;
            console.log('[SubscriptionContext] Auth.currentAuthenticatedUser() successful, userId:', userId); // Log: After Auth call

            const fetchUserQuery = /* GraphQL */ `
                query GetUserSubscription($id: ID!) {
                    getUser(id: $id) {
                        id
                        stripe_id
                        stripeStatus
                        createdAt
                    }
                }
            `;
            
            console.log('[SubscriptionContext] Attempting API.graphql for GetUserSubscription, userId:', userId); // Log: Before GQL call
            const userResponse: any = await API.graphql(graphqlOperation(fetchUserQuery, { id: userId }));
            console.log('[SubscriptionContext] API.graphql response for GetUserSubscription:', userResponse); // Log: After GQL call, raw response

            const userData = userResponse.data?.getUser;

            // Log the raw createdAt value received
            if (userData) {
                console.log('[SubscriptionContext] Raw userData.createdAt received for user ' + userId + ':', userData.createdAt);
            } else {
                console.log('[SubscriptionContext] No userData (userResponse.data.getUser is null/undefined) for user ' + userId + '. Full userResponse.data:', userResponse.data);
            }

            let finalStripeStatus: StripeStatus = 'free';
            let finalSubscriptionTier: SubscriptionTier = 'free';
            let finalHasActiveSubscription: boolean = false;
            let finalIsNewUserBonusActive: boolean = false;

            if (userData && userData.stripeStatus) {
                finalStripeStatus = userData.stripeStatus as StripeStatus;
            }

            // Determine tier and active status based on the finalStripeStatus
            if (finalStripeStatus === 'premium') {
                finalSubscriptionTier = 'premium';
                finalHasActiveSubscription = true;
            } else if (finalStripeStatus === 'light') {
                finalSubscriptionTier = 'light';
                finalHasActiveSubscription = true; 
            } else { 
                finalSubscriptionTier = 'free';
                finalHasActiveSubscription = false;
            }

            // Check for new user bonus based on createdAt
            if (userData && userData.createdAt) {
                try {
                    const createdAtDate = new Date(userData.createdAt);
                    const now = new Date();
                    const threeDaysInMillis = 3 * 24 * 60 * 60 * 1000;

                    if (now.getTime() - createdAtDate.getTime() < threeDaysInMillis) {
                        finalIsNewUserBonusActive = true;
                        console.log('[SubscriptionContext] New user bonus active for user:', userId);
                    } else {
                        console.log('[SubscriptionContext] New user bonus NOT active for user:', userId, 'account age (days):', (now.getTime() - createdAtDate.getTime()) / (1000 * 60 * 60 * 24));
                    }
                } catch (dateError) {
                    console.error('[SubscriptionContext] Error processing createdAt date for new user bonus:', dateError);
                    // Keep finalIsNewUserBonusActive as false if there's an error parsing date
                }
            }

            setStripeStatus(finalStripeStatus);
            setSubscriptionTier(finalSubscriptionTier);
            setHasActiveSubscription(finalHasActiveSubscription);
            setIsNewUserBonusActive(finalIsNewUserBonusActive); 
            setError(null); 

        } catch (err: any) {
            console.error('[SubscriptionContext] Error in fetchSubscriptionStatus:', err); // Log the full error object
            if (err.errors && err.errors.length > 0) {
                console.error('[SubscriptionContext] GraphQL errors:', JSON.stringify(err.errors, null, 2));
            }
            setError(err.message || 'Failed to fetch subscription status');
            setStripeStatus('free');
            setSubscriptionTier('free');
            setHasActiveSubscription(false);
            setIsNewUserBonusActive(false); 
        } finally {
            setIsLoading(false);
            console.log('[SubscriptionContext] fetchSubscriptionStatus finished'); // Log: Function exit (finally block)
        }
    };

    useEffect(() => {
        console.log('[SubscriptionContext] useEffect[] - calling fetchSubscriptionStatus'); // Log: useEffect trigger
        fetchSubscriptionStatus();
    }, []);

    const refreshSubscription = async () => {
        console.log('[SubscriptionContext] refreshSubscription called - calling fetchSubscriptionStatus'); // Log: refresh trigger
        await fetchSubscriptionStatus();
    };

    return (
        <SubscriptionContext.Provider
            value = {{
                isLoading,
                error,
                subscriptionTier,
                stripeStatus,
                hasActiveSubscription,
                refreshSubscription,
                isNewUserBonusActive 
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a subscription provider');
    }
    return context;
};